import Vue from 'vue';
import VueRouter, { Route } from 'vue-router';

import { tokenName } from '@/useAuth';

import Login from '@/views/Login.vue';
import Logout from '@/views/Logout.vue';
import AppLivePlatform from '@/views/AppLivePlatform.vue';
import AppLiveHelpers from '@/views/AppLiveHelpers.vue';

import VisitorIndex from '@/views/Visitor/Index.vue';
import VisitorList from '@/views/Visitor/List.vue';
import VisitorNew from '@/views/Visitor/New.vue';
import VisitorEditor from '@/views/Visitor/Edit.vue';

import TicketIndex from '@/views/Ticket/Index.vue';

import SpeakerIndex from '@/views/Speaker/Index.vue';
import SpeakerList from '@/views/Speaker/List.vue';
import SpeakerNew from '@/views/Speaker/New.vue';
import SpeakerEdit from '@/views/Speaker/Edit.vue';

import LiveModulePageColumn from '@/views/Live/Index.vue';
import LiveLandingPage from '@/views/Live/Landing.vue';
import JustARouter from '@/views/Live/JustARouter.vue';

import SessionNew from '@/views/Live/New.vue';
import SessionEdit from '@/views/Live/Edit.vue';

import LinkSetting from '@/views/Live/Link/Setting.vue';

import AgendaList from '@/views/Live/Agenda/List.vue';
import AgendaNew from '@/views/Live/Agenda/New.vue';
import AgendaEdit from '@/views/Live/Agenda/Edit.vue';

import VoteList from '@/views/Live/Vote/List.vue';
import VoteNew from '@/views/Live/Vote/New.vue';
import VoteEdit from '@/views/Live/Vote/Edit.vue';

import QuizList from '@/views/Live/Quiz/List.vue';
import QuizNew from '@/views/Live/Quiz/New.vue';
import QuizEdit from '@/views/Live/Quiz/Edit.vue';

// import QANew from '@/views/Live/QA/New.vue';
// import QAEdit from '@/views/Live/QA/Edit.vue';
import QAIndex from '@/views/Live/QA/Index.vue';
import QAList from '@/views/Live/QA/List.vue';
import QAChat from '@/views/Live/QA/Chat.vue';
import QAInitialSession from '@/views/Live/QA/Initial.vue';

import MenuSetting from '@/views/Live/Menu/Setting.vue';

import LiveChat from '@/views/Live/Chat/Index.vue';

import ConfigIndex from '@/views/Config/Index.vue';
import ConfigLanding from '@/views/Config/Landing.vue';
import ConfigLogin from '@/views/Config/Login.vue';
import ConfigRegister from '@/views/Config/Register.vue';
import ConfigWebsite from '@/views/Config/Website.vue';
import ConfigLive from '@/views/Config/Live.vue';
import ConfigSponsor from '@/views/Config/Sponsor.vue';
import ConfigMail from '@/views/Config/Mail.vue';

import UserIndex from '@/views/User/Index.vue';
import UserList from '@/views/User/List.vue';
import UserNew from '@/views/User/New.vue';
import UserEdit from '@/views/User/Edit.vue';

import StoreCreateProduct from '@/views/Store/CreateProduct.vue';
import StoreIndex from '@/views/Store/Index.vue';
import StoreList from '@/views/Store/List.vue';
import StoreProductEdit from '@/views/Store/EditProduct.vue';

import SettingIndex from '@/views/Setting/Index.vue';
import SettingProfile from '@/views/Setting/Profile.vue';

import TermsOfService from '@/views/TermsOfService.vue';
import Error from '@/views/Error.vue';
import Dashboard from '@/views/Dashboard.vue';

import FanCamIndex from '@/views/Live/FanCam/Index.vue';
import FanCamGreenscreen from '@/views/Live/FanCam/Greenscreen.vue'; // Lives under url: <root>/live-helpers/fancam-greenscreen

Vue.use(VueRouter);

const guardAuth = async (to: Route, from: Route, next: (route?: any) => void) => {
  if (localStorage.getItem(tokenName) || sessionStorage.getItem(tokenName)) {
    next(); // TODO: implement token expire check
  } else {
    next({ name: 'Login' });
  }
};

const routes = [
  { // when from: main dotify, main subdomain
    path: '/',
    redirect: { name: 'Login' },
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
  },
  {
    path: '/logout',
    name: 'Logout',
    component: Logout,
  },
  {
    path: '/terms-of-service',
    name: 'TermsOfService',
    component: TermsOfService,
  },
  {
    path: '/live-helpers',
    beforeEnter: guardAuth, // Check only root, no need to check children
    component: AppLiveHelpers,
    children: [
      {
        path: 'fan-cam-greenscreen/:roomID',
        component: FanCamGreenscreen,
        name: 'FanCamGreenscreen',
      },
    ],
  },
  {
    path: '/app',
    beforeEnter: guardAuth, // Check only root, no need to check children
    component: AppLivePlatform,
    redirect: 'Dashboard',
    name: 'App',
    children: [
      {
        path: '',
        component: Dashboard,
        name: 'Dashboard',
      },
      {
        path: 'fan-cam',
        component: FanCamIndex,
        name: 'FanCamIndex',
      },
      {
        path: 'visitor',
        component: VisitorIndex,
        redirect: 'VisitorList',
        name: 'VisitorIndex',
        children: [
          {
            path: '',
            component: VisitorList,
            name: 'VisitorList',
          },
          {
            path: 'new',
            component: VisitorNew,
            name: 'VisitorNew',
          },
          {
            path: ':id',
            component: VisitorEditor,
            name: 'VisitorEdit',
          },
        ],
      },
      {
        path: 'ticket',
        component: TicketIndex,
        name: 'TicketIndex',
        children: [
          // {
          //   path: '',
          //   component: VisitorList,
          //   name: 'VisitorList',
          // },
        ],
      },
      {
        path: 'speaker',
        component: SpeakerIndex,
        name: 'SpeakerIndex',
        redirect: { name: 'SpeakerList' },
        children: [
          {
            path: '',
            component: SpeakerList,
            name: 'SpeakerList',
          },
          {
            path: 'new',
            component: SpeakerNew,
            name: 'SpeakerNew',
          },
          {
            path: 'edit/:id',
            component: SpeakerEdit,
            name: 'SpeakerEdit',
          },
        ],
      },
      {
        path: 'store',
        component: StoreIndex,
        name: 'StoreIndex',
        children: [
          {
            path: '',
            component: StoreList,
            name: 'StoreList',
          },
          {
            path: ':id',
            component: StoreList,
            name: 'StoreListWithId',
          },
          {
            path: 'product/:productId',
            component: StoreProductEdit,
            name: 'StoreProductEdit',
          },
          {
            path: ':id/new',
            component: StoreCreateProduct,
            name: 'StoreCreateProduct',
          },
        ],
      },
      {
        path: 'new-session',
        component: SessionNew,
        name: 'SessionNew',
      },
      {
        path: 'edit-session/:id',
        component: SessionEdit,
        name: 'SessionEdit',
      },
      {
        path: 'link',
        component: LiveModulePageColumn,
        meta: { display: 'Link Streaming' },
        children: [
          {
            path: '',
            component: LiveLandingPage,
            name: 'LinkIndex',
            meta: { display: 'Link Streaming' },
          },
          {
            path: ':liveId',
            component: LinkSetting,
            name: 'LinkSetting',
            meta: { display: 'Link Streaming' },
          },
        ],
      },
      {
        path: 'agenda',
        component: LiveModulePageColumn,
        meta: { display: 'Agenda' },
        children: [
          {
            path: '',
            component: LiveLandingPage,
            name: 'AgendaIndex',
            meta: { display: 'Agenda' },
          },
          {
            path: ':liveId',
            component: JustARouter,
            name: 'AgendaWithLive',
            redirect: { name: 'AgendaList' },
            meta: { display: 'Agenda' },
            children: [
              {
                path: '',
                component: AgendaList,
                name: 'AgendaList',
                meta: { display: 'Agenda' },
              },
              {
                path: 'new',
                component: AgendaNew,
                name: 'AgendaNew',
                meta: { display: 'Agenda' },
              },
              {
                path: 'edit/:agendaId',
                component: AgendaEdit,
                name: 'AgendaEdit',
                meta: { display: 'Agenda' },
              },
            ],
          },
        ],
      },
      {
        path: 'vote',
        component: LiveModulePageColumn,
        meta: { display: 'Vote' },
        children: [
          {
            path: '',
            component: LiveLandingPage,
            name: 'VoteIndex',
            meta: { display: 'Vote' },
          },
          {
            path: ':liveId',
            component: JustARouter,
            name: 'VoteWithLive',
            redirect: { name: 'VoteList' },
            meta: { display: 'Vote' },
            children: [
              {
                path: '',
                component: VoteList,
                name: 'VoteList',
                meta: { display: 'Vote' },
              },
              {
                path: 'new',
                component: VoteNew,
                name: 'VoteNew',
                meta: { display: 'Vote' },
              },
              {
                path: 'edit/:voteId',
                component: VoteEdit,
                name: 'VoteEdit',
                meta: { display: 'Vote' },
              },
            ],
          },
        ],
      },
      {
        path: 'quiz',
        component: LiveModulePageColumn,
        meta: { display: 'Quiz & Ranking' },
        children: [
          {
            path: '',
            component: LiveLandingPage,
            name: 'QuizIndex',
            meta: { display: 'Quiz & Ranking' },
          },
          {
            path: ':liveId',
            component: JustARouter,
            name: 'QuizWithLive',
            redirect: { name: 'QuizList' },
            meta: { display: 'Quiz & Ranking' },
            children: [
              {
                path: '',
                component: QuizList,
                name: 'QuizList',
                meta: { display: 'Quiz & Ranking' },
              },
              {
                path: 'new',
                component: QuizNew,
                name: 'QuizNew',
                meta: { display: 'Quiz & Ranking' },
              },
              {
                path: 'edit/:quizId',
                component: QuizEdit,
                name: 'QuizEdit',
                meta: { display: 'Quiz & Ranking' },
              },
            ],
          },
        ],
      },
      {
        path: 'qa',
        component: LiveModulePageColumn,
        meta: { display: 'Q & A' },
        children: [
          {
            path: '',
            component: LiveLandingPage,
            name: 'QAIndex',
            meta: { display: 'Q & A' },
          },
          {
            path: ':liveId',
            component: QAList,
            name: 'QAWithLive',
            redirect: '',
            meta: { display: 'Q & A' },
            children: [
              {
                path: 'initial',
                component: QAInitialSession,
                name: 'QAInitialSession',
                meta: { display: 'เริ่มการสนทนาใหม่' },
              },
              {
                path: ':userId',
                component: QAChat,
                name: 'QAChatUser',
                meta: { display: 'Q & A' },
              },
            ],
          },
        ],
      },
      {
        path: 'menu',
        component: LiveModulePageColumn,
        meta: { display: 'Setting' },
        children: [
          {
            path: '',
            component: LiveLandingPage,
            name: 'MenuIndex',
            meta: { display: 'Setting' },
          },
          {
            path: ':liveId',
            component: MenuSetting,
            name: 'MenuSetting',
            meta: { display: 'Setting' },
          },
        ],
      },
      {
        path: 'live-chat',
        component: LiveModulePageColumn,
        meta: { display: 'Live Chat' },
        children: [
          {
            path: '',
            component: LiveLandingPage,
            name: 'LiveChatIndex',
            meta: { display: 'Live Chat' },
          },
          {
            path: ':liveId',
            component: LiveChat,
            name: 'LiveChat',
            meta: { display: 'Live Chat' },
          },
        ],
      },
      {
        path: 'config',
        component: ConfigIndex,
        children: [
          {
            path: 'landing',
            component: ConfigLanding,
            name: 'ConfigLanding',
          },
          {
            path: 'login',
            component: ConfigLogin,
            name: 'ConfigLogin',
          },
          {
            path: 'register',
            component: ConfigRegister,
            name: 'ConfigRegister',
          },
          {
            path: 'website',
            component: ConfigWebsite,
            name: 'ConfigWebsite',
          },
          {
            path: 'live',
            component: ConfigLive,
            name: 'ConfigLive',
          },
          {
            path: 'sponsor',
            component: ConfigSponsor,
            name: 'ConfigSponsor',
          },
          {
            path: 'mail',
            component: ConfigMail,
            name: 'ConfigMail',
          },
        ],
      },
      {
        path: 'user',
        component: UserIndex,
        children: [
          {
            path: '',
            name: 'UserIndex',
            component: UserList,
          },
          {
            path: ':roleId',
            name: 'UserList',
            component: UserList,
          },
          {
            path: 'new/:initRole',
            name: 'UserNew',
            component: UserNew,
            props: true,
          },
          {
            path: 'edit/:userId',
            name: 'UserEdit',
            component: UserEdit,
          },
        ],
      },
      {
        path: 'setting',
        component: SettingIndex,
        name: 'SettingIndex',
        redirect: 'SettingProfile',
        children: [
          {
            path: '',
            component: SettingProfile,
            name: 'SettingProfile',
          },
        ],
      },
    ],
  },
  { path: '*', component: Error },
];

const router = new VueRouter({
  routes,
});

export default router;
